import { safeJsonParse } from '@visiba/std';
import { Analytic, MatomoTracker } from '@visiba/analytic';
import { HostingService } from '@application/hosting.service';
import { Injectable } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { ConsentService, UserConsent } from '@component/consent/consent.service';

export enum AnalyticCategories {
	Unset = 'Generic',
	Window = 'Window',
	TopNavigation = 'Top-navigation',
	StartView = 'Start-view',
	AttentionCards = 'Attention cards',
	SearchReception = 'SearchReception-popup',
	PatientCall = 'Patient-Call',
	CallChat = 'Call-Chat',
	CallBar = 'Call-Bar',
	CallErrors = 'Call-Errors',
	DeviceSettings = 'Call-Device-Settings',
	CallScene = 'Call-Scene',
	CallLobby = 'Call-Lobby',
	ReceptionHome = 'ReceptionHome',
	BrowseReceptionView = 'BrowseReception-view',
	DeviceGateway = 'Device-Gateway',
	Visits = 'Visits',
	VisitsAppointment = 'Visits-Appointment',
	VisitsChat = 'Visits-Chat',
	PaymentFlow = 'Payment-Flow',
	DynamicForm = 'Dynamic-Form',
	Footer = 'Footer',
	SignIn = 'Sign-In',
	NavigationMenu = 'Navigation-Menu',
	Guest = 'Guest',
	CreateNewMessage = 'Create-New-Message',
	TopBar = 'Top-Bar',
	MyAccount = 'My-Account',
	Booking = 'Booking',
	RedRobin = 'RedRobin',
	CookieConsent = 'CookieConsent',
	Error = 'Error',
	UnhandledError = 'Unhandled Error',
}

export enum AnalyticActions {
	Clicked = 'Clicked',
	ChangedLanguage = 'Changed language to',
}

export type AnalyticAction = `${AnalyticActions}: ${string}`;

export class InvalidAnalyticConfig extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'InvalidAnalyticConfig';
		Object.setPrototypeOf(this, InvalidAnalyticConfig.prototype);
	}
}

interface Config {
	licenceHolderId: number;
	globalCustomerId: string;
	siteId: number;
	url: string;
}

@Injectable()
export class AnalyticService {
	private tracker: Nullable<MatomoTracker> = null;
	private readonly logger = new Logger('analytic');
	private config: Nullable<Config> = null;

	constructor(
		private readonly hostingService: HostingService,
		private readonly consentService: ConsentService,
	) {
		// Empty
	}

	/**
	 * Needs to be called after the LicenceHolder service has had its data populated.
	 */
	public setup(licenceHolderId: number, globalCustomerId: string): void {
		const analyticConfig = this.hostingService.analyticConfig;
		if (!analyticConfig.enabled) {
			this.logger.logDebug('Analytic is disabled. No tracking will be done.');

			return;
		}

		this.config = {
			licenceHolderId: licenceHolderId,
			globalCustomerId: globalCustomerId,
			siteId: analyticConfig.meta.siteId,
			url: analyticConfig.meta.url,
		};

		this.createTracker();
	}

	/**
	 * Creates a new tracker instance with the correct Visiba settings.
	 * If one already exists it will be killed and a new one will be created.
	 */
	public createTracker() {
		if (this.config == null) {
			this.logger.logError('No config was set up for the analytic service');

			return;
		}

		if (this.tracker != null) {
			this.logger.logVerbose('A tracker was already initiated and was now killed - bye bye!');
			this.tracker.kill();
		}

		// We're rolling double/tripple negation here but: no consent -> no cookies.
		const mayUseCookie = this.consentService.hasConsent(UserConsent.Analytic);
		const useDisableCookies = mayUseCookie === false;

		this.logger.logVerbose('A new tracker was created. May use cookies: {mayUseCookie}', mayUseCookie);

		this.tracker = Analytic.createTracker({
			siteId: this.config.siteId.toString(),
			useEnableCrossDomainLinking: false,
			// Important that this adheres to the user's cookie choice. Else it'll not be legal.
			useDisableCookies: useDisableCookies,
		});
		this.tracker.setCustomVariable({
			index: 1,
			category: 'LicenceHolderId',
			value: this.config.licenceHolderId.toString(),
			scope: 'page',
		});
		this.tracker.setCustomVariable({
			index: 3,
			category: 'GlobalCustomerId',
			value: this.config.globalCustomerId,
			scope: 'page',
		});
		this.tracker.setCustomVariable({
			index: 4,
			category: 'Instance',
			value: location.hostname,
			scope: 'page',
		});
		this.tracker.setCustomVariable({
			index: 5,
			category: 'Space',
			value: 'V4', // V4 == the fourth iteration of the patient web. Ask some old räv for more info!
			scope: 'page',
		});

		Analytic.initialize(this.config.url);
	}

	public trackEvent(category: AnalyticCategories, action: string, name?: string, value?: number): void {
		if (name !== undefined && value !== undefined) {
			this.logger.logVerbose(
				'trackEvent: category: "{category}", action: "{action}", name: "{name}", value: "{value}"',
				category,
				action,
				name,
				value,
			);
		} else if (name !== undefined) {
			this.logger.logVerbose('trackEvent: category: "{category}", action: "{action}", name: "{name}"', category, action, name);
		} else {
			this.logger.logVerbose('trackEvent: category: "{category}", action: "{action}"', category, action);
		}

		if (this.tracker == null) {
			this.logger.logDebug('In trackEvent. No analytic instance has been created');

			return;
		}

		try {
			this.tracker.trackEvent({
				action,
				category,
				name,
				value,
			});
		} catch (error: unknown) {
			this.logger.logError('could not track event {category}: "{action}"', category, action);
		}
	}

	public trackPageView(...args: Parameters<MatomoTracker['trackPageView']>): void {
		this.logger.logVerbose('trackPageView: "{title}"', args[0].customTitle);

		if (this.tracker == null) {
			this.logger.logDebug('In trackPageView. No analytic instance has been created');

			return;
		}

		try {
			this.tracker.trackPageView(...args);
		} catch (error: unknown) {
			this.logger.logError('could not track event {args}', safeJsonParse(args));
		}
	}

	public changeTrackedUnitId(unitId: number): void {
		if (this.tracker == null) {
			this.logger.logDebug('In changeTrackedUnitId. No analytic instance has been created');

			return;
		}

		this.tracker.setCustomVariable({
			index: 2,
			category: 'UnitId',
			value: unitId.toString(),
			scope: 'page',
		});
	}
}
